import axios from "axios";
import store from "@/store";

class OfflineQueueService {
  constructor() {
    this.queueKey = "offlineRequestQueue";
    this.isSyncing = false;

    if (typeof window !== "undefined") {
      this.updateQueueCount(this.readQueue());
      window.addEventListener("online", () => {
        this.flush();
      });
    }
  }

  isOnline() {
    if (typeof navigator === "undefined") {
      return true;
    }
    return navigator.onLine;
  }

  isNetworkError(error) {
    return !error || !error.response;
  }

  shouldQueue(config) {
    const method = (config.method || "get").toLowerCase();
    return method !== "get";
  }

  canQueueData(data) {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      return false;
    }
    return true;
  }

  requestKey(config) {
    const method = (config.method || "get").toLowerCase();
    const base = `${method}:${config.baseURL || ""}:${config.url || ""}`;
    if (method === "post") {
      const data = this.parseConfigData(config);
      const clientId = this.getClientRequestId(data);
      if (clientId) {
        return `${base}:client:${clientId}`;
      }
      if (data && typeof data === "object" && data.id) {
        return `${base}:id:${data.id}`;
      }
      return `${base}:ts:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    }
    return base;
  }

  readQueue() {
    if (typeof window === "undefined") {
      return [];
    }
    const raw = localStorage.getItem(this.queueKey);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  writeQueue(queue) {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.setItem(this.queueKey, JSON.stringify(queue));
    this.updateQueueCount(queue);
  }

  updateQueueCount(queue) {
    store.commit("app/setOfflineQueueCount", queue.length);
  }

  sanitizeHeaders(headers) {
    if (!headers) {
      return {};
    }
    const next = { ...headers };
    delete next.Authorization;
    delete next.authorization;
    return next;
  }

  enqueue(config) {
    if (!config || !this.shouldQueue(config) || !this.canQueueData(config.data)) {
      return false;
    }

    const queue = this.readQueue();
    const key = this.requestKey(config);
    const payload = {
      key,
      method: (config.method || "get").toLowerCase(),
      url: config.url,
      baseURL: config.baseURL,
      data: config.data,
      params: config.params,
      headers: this.sanitizeHeaders(config.headers),
      queuedAt: Date.now()
    };

    let replaced = false;
    if (payload.method !== "post" || key.includes(":id:") || key.includes(":client:")) {
      for (let i = queue.length - 1; i >= 0; i--) {
        if (queue[i].key === key) {
          queue[i] = payload;
          replaced = true;
          break;
        }
      }
    }

    if (!replaced) {
      queue.push(payload);
    }

    this.writeQueue(queue);
    return true;
  }

  async flush() {
    if (this.isSyncing || !this.isOnline()) {
      return;
    }
    const queue = this.readQueue();
    if (queue.length === 0) {
      return;
    }

    this.isSyncing = true;
    let remaining = [];

    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      try {
        await axios({
          method: item.method,
          url: item.url,
          baseURL: item.baseURL,
          data: item.data,
          params: item.params,
          headers: item.headers
        });
      } catch (error) {
        if (this.isNetworkError(error)) {
          remaining = queue.slice(i);
          break;
        }
      }
    }

    this.writeQueue(remaining);
    this.isSyncing = false;

    if (queue.length > 0 && remaining.length === 0) {
      store.dispatch("app/showOfflineSynced");
    }
  }

  buildOfflineResponse(config) {
    return {
      data: this.buildOfflineData(config),
      status: 202,
      statusText: "Accepted (offline)",
      config,
      headers: {}
    };
  }

  parseConfigData(config) {
    if (!config || config.data == null) {
      return null;
    }
    let data = config.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (err) {
        return data;
      }
    }
    return data;
  }

  getClientRequestId(data) {
    if (!data || typeof data !== "object") {
      return null;
    }
    return data.client_id || data.clientId || data.temp_id || data.tempId || data.uuid || null;
  }

  buildOfflineData(config) {
    let data = this.parseConfigData(config);
    if (data == null) {
      return null;
    }
    const method = (config.method || "get").toLowerCase();
    if (method === "post" && data && typeof data === "object" && !Array.isArray(data)) {
      if (!data.id) {
        data = { ...data, id: this.generateTempId(), _offline: true };
      }
    }
    return data;
  }

  generateTempId() {
    return -Date.now();
  }
}

export default new OfflineQueueService();
